import { Check, Minus, X } from "lucide-react";
import { cn } from "@/lib/utils";
import type { CompanyProfile, OpportunityScore, LicitacionMock as Licitacion } from "@/types";
import { formatCLP } from "@/lib/mock-data";
import { levelLabel } from "@/lib/intelligence/scoring";

export interface ComparativaRow {
  factor: string;
  empresa: string;
  /** Lado del proceso: la licitación o la Compra Ágil, según quién la use. */
  licitacion: string;
  match: "ok" | "warn" | "fail";
}

type Row = ComparativaRow;

/**
 * Presentación de la comparativa perfil ↔ proceso. Separada de la construcción
 * de filas para que Compra Ágil IA la reutilice con sus propios campos, en vez
 * de duplicar la tabla entera.
 */
export function ComparativaTable({
  rows,
  columnaProceso = "Licitación",
}: {
  rows: ComparativaRow[];
  columnaProceso?: string;
}) {
  return (
    <div className="overflow-hidden rounded-xl border border-border">
      <table className="w-full text-sm">
        <thead className="bg-muted/40 text-xs uppercase tracking-wider text-muted-foreground">
          <tr>
            <th className="text-left font-medium px-4 py-2.5">Factor</th>
            <th className="text-left font-medium px-4 py-2.5">Empresa</th>
            <th className="text-left font-medium px-4 py-2.5">{columnaProceso}</th>
            <th className="text-right font-medium px-4 py-2.5">Match</th>
          </tr>
        </thead>
        <tbody className="divide-y divide-border">
          {rows.map((r) => (
            <tr key={r.factor} className="hover:bg-muted/30">
              <td className="px-4 py-3 font-medium text-foreground">{r.factor}</td>
              <td className="px-4 py-3 text-muted-foreground">{r.empresa}</td>
              <td className="px-4 py-3 text-muted-foreground">{r.licitacion}</td>
              <td className="px-4 py-3 text-right">
                <MatchIcon match={r.match} />
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

export function CompanyVsTenderTable({
  profile,
  licitacion,
  scoring,
}: {
  profile: CompanyProfile;
  licitacion: Licitacion;
  scoring: OpportunityScore;
}) {
  const rows: Row[] = [
    {
      factor: "Rubro / Categoría",
      empresa: profile.datos.rubros.slice(0, 2).join(", "),
      licitacion: licitacion.categoria,
      match: profile.datos.rubros.includes(licitacion.categoria) ? "ok" : "warn",
    },
    {
      factor: "Región",
      empresa: profile.estrategia.regionesPreferidas.join(", "),
      licitacion: licitacion.region,
      match: profile.estrategia.regionesPreferidas.includes(licitacion.region)
        ? "ok"
        : profile.datos.regiones.includes(licitacion.region)
          ? "warn"
          : "fail",
    },
    {
      factor: "Modalidad",
      empresa: profile.estrategia.modalidadesPreferidas.join(", "),
      licitacion: licitacion.modalidad,
      match: profile.estrategia.modalidadesPreferidas.includes(licitacion.modalidad)
        ? "ok"
        : "warn",
    },
    {
      factor: "Presupuesto",
      empresa: `${formatCLP(profile.estrategia.presupuestoMin)} — ${formatCLP(profile.estrategia.presupuestoMax)}`,
      licitacion: formatCLP(licitacion.montoEstimado),
      match:
        licitacion.montoEstimado >= profile.estrategia.presupuestoMin &&
        licitacion.montoEstimado <= profile.estrategia.presupuestoMax
          ? "ok"
          : "warn",
    },
    {
      factor: "Riesgo",
      empresa: `Tolerancia ${profile.estrategia.toleranciaRiesgo}`,
      licitacion: `Riesgo ${licitacion.riesgo}`,
      match: licitacion.riesgo === "bajo" ? "ok" : licitacion.riesgo === "medio" ? "warn" : "fail",
    },
    {
      factor: "Complejidad",
      empresa: `Capacidad ${profile.estrategia.complejidadAceptada}`,
      licitacion: `Estimada ${levelLabel(scoring.complejidad)}`,
      match:
        scoring.complejidad === "bajo" ? "ok" : scoring.complejidad === "medio" ? "warn" : "fail",
    },
    {
      factor: "Certificaciones",
      empresa: profile.capacidades.certificaciones.slice(0, 3).join(", "),
      licitacion: "ISO 27001, ISO 9001 sugeridas",
      match: "ok",
    },
    {
      factor: "Historial organismo",
      empresa: `${profile.historial.organismosFrecuentes.find((o) => o.nombre === licitacion.organismo)?.adjudicadas ?? 0} adjudicaciones`,
      licitacion: licitacion.organismo,
      match: profile.historial.organismosFrecuentes.some((o) => o.nombre === licitacion.organismo)
        ? "ok"
        : "warn",
    },
  ];

  return <ComparativaTable rows={rows} />;
}

function MatchIcon({ match }: { match: "ok" | "warn" | "fail" }) {
  if (match === "ok") return <Check className={cn("inline size-4 text-success")} />;
  if (match === "warn") return <Minus className={cn("inline size-4 text-warning")} />;
  return <X className={cn("inline size-4 text-destructive")} />;
}
